import { useState, useCallback } from "react";
import {
  createFileUploadSession,
  uploadFilePart,
  completeFileUpload,
  createPublicFileUploadSession,
  uploadPublicFilePart,
  completePublicFileUpload,
  directPublicUpload,
} from "../lib/api-client";

const MIN_PART_SIZE = 5 * 1024 * 1024; // 5 MB minimum for R2 multipart uploads
const _MAX_PART_SIZE = 5 * 1024 * 1024 * 1024; // 5 GB maximum
const DEFAULT_PART_SIZE = 10 * 1024 * 1024; // 10 MB default

/**
 * Convert ArrayBuffer to base64 string without causing stack overflow.
 * Uses chunked processing to avoid hitting call stack limits with large files.
 */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const CHUNK_SIZE = 8192; // Process 8KB at a time to avoid stack overflow
  let binary = "";

  for (let i = 0; i < bytes.length; i += CHUNK_SIZE) {
    const chunk = bytes.subarray(i, Math.min(i + CHUNK_SIZE, bytes.length));
    binary += String.fromCharCode(...chunk);
  }

  return btoa(binary);
}

export interface UploadProgress {
  uploaded: number;
  total: number;
  percentage: number;
  status: "idle" | "uploading" | "completing" | "completed" | "error";
  error?: string;
}

export interface FileUploadOptions {
  formId: string;
  shareToken?: string; // Optional share token for public uploads
  onProgress?: (progress: UploadProgress) => void;
  partSize?: number; // Size of each part in bytes
}

export interface UploadedFile {
  key: string;
  uploadId: string;
  filename: string;
  size: number;
  contentType: string;
}

export function useFileUpload() {
  const [progress, setProgress] = useState<UploadProgress>({
    uploaded: 0,
    total: 0,
    percentage: 0,
    status: "idle",
  });

  const uploadFile = useCallback(
    async (file: File, options: FileUploadOptions): Promise<UploadedFile> => {
      const { formId, shareToken, onProgress, partSize = DEFAULT_PART_SIZE } = options;

      // Validate file
      if (file.size === 0) {
        throw new Error("File is empty");
      }

      // For small files, use simple upload (we'll implement this later)
      // For now, always use multipart for consistency
      const useMultipart = file.size >= MIN_PART_SIZE;

      const updateProgress = (updates: Partial<UploadProgress>) => {
        setProgress((prev) => {
          const next = { ...prev, ...updates };
          onProgress?.(next);
          return next;
        });
      };

      try {
        updateProgress({ status: "uploading", total: file.size });

        // FAST PATH: For small files with shareToken, use direct binary upload (no base64, single request)
        if (shareToken && file.size < MIN_PART_SIZE) {
          const result = await directPublicUpload(formId, shareToken, file);
          updateProgress({ status: "completed", uploaded: file.size, percentage: 100 });
          return {
            key: result.key,
            uploadId: "direct",
            filename: file.name,
            size: file.size,
            contentType: file.type || "application/octet-stream",
          };
        }

        // MULTIPART PATH: For large files or authenticated uploads
        // Step 1: Create upload session (use public endpoint if shareToken provided)
        const session = shareToken
          ? await createPublicFileUploadSession(formId, shareToken, {
            filename: file.name,
            contentType: file.type || "application/octet-stream",
            sizeBytes: file.size,
          })
          : await createFileUploadSession(formId, {
            filename: file.name,
            contentType: file.type || "application/octet-stream",
            sizeBytes: file.size,
          });

        if (!useMultipart || file.size < MIN_PART_SIZE) {
          // For small files, we could use a simple PUT, but for now use single-part multipart
          const fileData = await file.arrayBuffer();
          const base64Data = arrayBufferToBase64(fileData);

          const part = shareToken
            ? await uploadPublicFilePart(formId, session.uploadId, 1, shareToken, {
              key: session.key,
              data: base64Data,
            })
            : await uploadFilePart(formId, session.uploadId, 1, {
              key: session.key,
              data: base64Data,
            });

          if (shareToken) {
            await completePublicFileUpload(formId, session.uploadId, shareToken, {
              key: session.key,
              parts: [{ partNumber: 1, etag: part.etag }],
            });
          } else {
            await completeFileUpload(formId, session.uploadId, {
              key: session.key,
              filename: file.name,
              contentType: file.type || "application/octet-stream",
              sizeBytes: file.size,
              parts: [{ partNumber: 1, etag: part.etag }],
            });
          }

          updateProgress({ status: "completed", uploaded: file.size, percentage: 100 });

          return {
            key: session.key,
            uploadId: session.uploadId,
            filename: file.name,
            size: file.size,
            contentType: file.type || "application/octet-stream",
          };
        }

        // Step 2: Calculate parts
        const totalParts = Math.ceil(file.size / partSize);
        const parts: Array<{ partNumber: number; etag: string }> = [];

        // Step 3: Upload each part
        for (let i = 0; i < totalParts; i++) {
          const start = i * partSize;
          const end = Math.min(start + partSize, file.size);
          const partData = file.slice(start, end);
          const partNumber = i + 1;

          const arrayBuffer = await partData.arrayBuffer();
          const base64Data = arrayBufferToBase64(arrayBuffer);

          const part = shareToken
            ? await uploadPublicFilePart(formId, session.uploadId, partNumber, shareToken, {
              key: session.key,
              data: base64Data,
            })
            : await uploadFilePart(formId, session.uploadId, partNumber, {
              key: session.key,
              data: base64Data,
            });

          parts.push({ partNumber, etag: part.etag });

          const uploaded = Math.min(end, file.size);
          const percentage = Math.round((uploaded / file.size) * 100);
          updateProgress({
            uploaded,
            percentage,
          });
        }

        // Step 4: Complete the upload
        updateProgress({ status: "completing" });

        if (shareToken) {
          await completePublicFileUpload(formId, session.uploadId, shareToken, {
            key: session.key,
            parts: parts.sort((a, b) => a.partNumber - b.partNumber),
          });
        } else {
          await completeFileUpload(formId, session.uploadId, {
            key: session.key,
            filename: file.name,
            contentType: file.type || "application/octet-stream",
            sizeBytes: file.size,
            parts: parts.sort((a, b) => a.partNumber - b.partNumber),
          });
        }

        updateProgress({ status: "completed", uploaded: file.size, percentage: 100 });

        return {
          key: session.key,
          uploadId: session.uploadId,
          filename: file.name,
          size: file.size,
          contentType: file.type || "application/octet-stream",
        };
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Upload failed";
        updateProgress({ status: "error", error: errorMessage });
        throw error;
      }
    },
    []
  );

  return { uploadFile, progress };
}
